/**
 * How good a model is AT THE JOB BEING PRICED — not in general.
 *
 * The price side of this tool is measured: real token counts, real rate cards.
 * The capability side cannot be, because every project in the corpus was built
 * with Claude. So capability comes from PUBLISHED benchmarks, cited, and split by
 * domain — because "which model is best" has no answer until you say at what.
 *
 * Two rules hold this together, and both exist to stop the table from lying:
 *
 *  1. A score is never invented. A model with no published result in the domain
 *     of the task gets `null`, sits out of the recommendations, and says so.
 *  2. Scores from two different benchmarks are never compared in one ranking.
 *     An Intelligence Index point and a Coding Agent Index point are not the same
 *     unit; averaging them would produce a number nobody measured.
 *
 * Everything here is normalised to 0..1 so the selection maths below is unitless.
 */

export type Domain = 'coding_agent' | 'general'

export interface DomainSource {
  id: Domain
  /** What kind of work this index is evidence for, in the reader's words. */
  label_fr: string
  source_fr: string
  /** La même date, dite en anglais : « relevé le 11/09/2026 » n'est pas lisible partout. */
  fetched_en: string
  url: string
  fetched_fr: string
  measures_fr: string
  caveat_fr: string
  /** 0..1. null = no published result for that model; never guessed. */
  scores: Record<string, number | null>
}

/**
 * Coding: Artificial Analysis Coding Agent Index v1.5 — the only per-task index
 * published per model that covers what this tool prices. Composite of DeepSWE
 * v1.1 (113 software engineering tasks), Terminal-Bench 4.0 (66 agentic terminal
 * tasks) and SWE-Atlas-QnA (124 technical Q&A), each scored pass@1 over three
 * attempts, equally weighted.
 *
 * Coverage is the catch: it publishes 15 rows, and only these eight are models
 * this tool prices. Two near-misses are deliberately NOT mapped, because a version
 * number is not a detail — the index has DeepSeek V4 Flash 0731 (we price v4.1) and
 * Muse Spark, which is not in the grid at all. Borrowing those scores would put a
 * number next to a model that never sat the exam.
 *
 * Gemini 3.8 Flash was one of those near-misses until 17/09/2026, when the grid stopped
 * pricing Gemini 3.5 Flash and started pricing 3.8 — the one Google actually serves. The
 * index does publish a coding score for it; it is absent here for the same reason as Sol's:
 * nobody has read it off the source page yet. A missing score is a missing measurement.
 *
 * GPT-5.6 Sol used to be on that list for a reason that has since become false: it
 * said we priced Terra and not Sol, and the catalogue prices both. Sol is in the grid
 * now, carrying its published GENERAL score (0.47, read 14/09/2026). Its CODING score
 * exists in the index and is deliberately absent here — I could not retrieve the page,
 * so it stays null and Sol is excluded from coding recommendations until someone reads
 * it off the source. A missing score is a missing measurement, not a zero.
 *
 * DeepSeek V4 Pro is not here either, and it is a different case: the index scores it
 * (0.431) and the price catalogue still lists it, but the endpoint REDIRECTS to
 * v4.1-flash. Serving it would quote a price for a model that never answers, so it is
 * out of the grid and its scores are out of this table.
 *
 * One row is a family match rather than an exact one: the index publishes
 * `qwen3-8-max-public` and we price the `0902` snapshot of Qwen3.8 Max. Same
 * model, different build date. It is flagged here rather than left silent.
 */
const CODING_AGENT: DomainSource = {
  id: 'coding_agent',
  label_fr: 'écrire du code',
  source_fr: 'Artificial Analysis — Coding Agent Index v1.5',
  url: 'https://artificialanalysis.ai/agents/coding-agents',
  fetched_fr: 'relevé le 11/09/2026',
  fetched_en: 'read on 11/09/2026',
  measures_fr: 'Trois épreuves de développement réel — corriger du code sur 113 '
             + 'tâches, tenir un terminal sur 66, répondre à 124 questions '
             + 'techniques — chacune jouée trois fois.',
  caveat_fr: 'Chaque note est celle d’un modèle DANS un harnais donné (Claude Code, '
           + 'Codex, Opencode…), et le harnais compte autant que le modèle. Elle est '
           + 'aussi relevée au réglage de raisonnement le plus poussé, donc le plus '
           + 'cher. Une exception : l’index ne note pas DeepSeek V4.1 Flash sur le code. '
           + 'Sa note vient de nos quatre tâches de code, jouées une seule fois en '
           + 'raisonnement maximal dans le harnais de DeepSeek et notées à l’aveugle '
           + 'face à GLM-5.3-Flash. Claude Opus 5 a affronté GLM-5.3-Flash sur la même '
           + 'grille, figée, un autre jour : DeepSeek et lui n’ont jamais été notés côte à '
           + 'côte. À travers ce modèle-témoin, Claude Opus 5 devance DeepSeek sur chacune '
           + 'des quatre tâches de code. La note est ramenée sur '
           + 'l’échelle de l’index par un simple rapport à Claude Opus 5, sans jamais '
           + 'pouvoir égaler la sienne, et elle reste incertaine : selon le modèle pris '
           + 'comme repère, elle irait de 44 à 59, et les trois modèles notés des deux '
           + 'côtés ne se classent pas pareil chez nous et dans l’index. Sur nos tâches, '
           + 'DeepSeek semble devant GLM-5.3, sur une seule série et à confirmer ; quatre '
           + 'tâches ne disent pas encore si son retard sur Claude Opus 5 est faible ou '
           + 'd’une dizaine de points.',
  scores: {
    'anthropic/claude-fable-5.1': 0.622,   // Claude Code - Fable 5.1 (max)
    'openai/gpt-6-astra': 0.616,           // Codex - GPT-6 Astra (max)
    'anthropic/claude-opus-5': 0.597,      // Claude Code - Opus 5 (max)
    'z-ai/glm-5.3': 0.536,                 // Opencode - GLM-5.3
    'moonshotai/kimi-k3': 0.519,           // Kimi Code CLI - Kimi K3
    'x-ai/grok-4.6': 0.470,                // Grok Build - Grok 4.6 (xhigh)
    'qwen/qwen3.8-max-0902': 0.433,        // Claude Code - Qwen3.8 Max
  },
}

/**
 * Everything else: the generalist Intelligence Index. It is the right evidence
 * for "read this, decide, write that" work and the wrong evidence for building a
 * product — which is precisely why the coding tiles do not use it.
 */
const GENERAL: DomainSource = {
  id: 'general',
  label_fr: 'du travail de tête général',
  source_fr: 'Artificial Analysis — Intelligence Index',
  url: 'https://artificialanalysis.ai/leaderboards/models',
  fetched_fr: 'relevé le 20/09/2026',
  fetched_en: 'read on 20/09/2026',
  measures_fr: 'Un indice généraliste : raisonnement, connaissances, calcul. Il '
             + 'classe, il ne garantit rien sur une tâche précise.',
  caveat_fr: 'Relevé au réglage de raisonnement le plus poussé, qui est aussi le '
           + 'plus cher.',
  //  Un relevé daté, pas une constante : l'indice bouge sous des modèles qui n'ont pas changé.
  //  Entre le 11 et le 20/09/2026, Haiku est passé de 18 à 17, DeepSeek V4.1 Flash de 40 à 39 et
  //  MiniMax M3 de 30 à 29 — ce dernier pose le plafond, qui suit. Tous les relevés sont gardés
  //  dans data/index-history.json, et tests/capability.test.ts vérifie que cette table EST le
  //  dernier d'entre eux.
  scores: {
    'anthropic/claude-opus-5': 0.51,
    'anthropic/claude-opus-4.6': null,
    'anthropic/claude-sonnet-5': 0.38,
    'anthropic/claude-haiku-4.5': 0.17,
    'openai/gpt-5.6-terra': 0.42,
    'openai/gpt-5.6-sol': 0.47,
    'openai/gpt-5.4-mini': null,
    'x-ai/grok-4.6': 0.44,
    // Relevé le 17/09/2026 sur la page du modèle : 41 au réglage HAUT (#29/199), 34 au
    // réglage BAS (#56/199). La table prend le réglage le plus poussé, comme pour tous les
    // autres — et notre bench d'automatisation, lui, l'a fait tourner en bas.
    'google/gemini-3.8-flash': 0.41,
    'deepseek/deepseek-v4.1-flash': 0.39,
    'anthropic/claude-fable-5.1': 0.53,
    'openai/gpt-6-astra': 0.53,
    'moonshotai/kimi-k3': 0.44,
    'z-ai/glm-5.3': 0.45,
    'z-ai/glm-5.3-flash': 0.42,
    'minimax/minimax-m3': 0.29,
    // Le 40 d'avant le 19/09/2026 était la note d'une autre ligne du classement, Qwen3.8 2.4T
    // A95B, sa variante open-weight — cinq points d'écart, donc un autre modèle, pas un autre
    // build.
    'qwen/qwen3.8-max-0902': 0.45,
  },
}

export const DOMAINS: Record<Domain, DomainSource> = {
  coding_agent: CODING_AGENT,
  general: GENERAL,
}

/**
 * Our own measurements, when we have them.
 *
 * Published indices answer "can this model do software engineering"; they do not
 * answer "can this model build a landing page the way we build landing pages".
 * Running the same task on several models and reading back the result is the only
 * thing that would. A model absent from this map is a missing measurement, not a zero.
 *
 * Filling one entry is the whole update path: a score here wins over the
 * published index and the interface relabels itself as measured by us.
 */
export const BENCH: Partial<Record<Domain, Record<string, number>>> = {
  coding_agent: {
    // NOT raw grid notes: coding means (4 tasks: instagram-mobile, ged-webapp,
    // coffee-ecommerce, french-a2), /50, put on the FROZEN grid (R4) through the
    // link model GLM-5.3-Flash, which sat both rounds. Only their RATIO is used.
    // Recomputed from data/ by tests/capability.test.ts — change the data, not these.
    //   opus-5    R5 (16–17/09, frozen grid, MAX effort, Claude Code, run 2): 42.625,
    //             Flash 36.50 -> 42.625 × 34.25/36.50
    //   deepseek  R4 (15/09, frozen grid, MAX reasoning, DeepSeek Harness): 36.375, Flash 34.25
    'anthropic/claude-opus-5': (42.625 * 34.25 / 36.5) / 50,   // 0.79995
    'deepseek/deepseek-v4.1-flash': 36.375 / 50,               // 0.7275
    'z-ai/glm-5.3-flash': 34.25 / 50,                          // 0.685, le modèle-témoin
  },
}

/**
 * Les mêmes mesures, tâche par tâche, au lieu de leur moyenne.
 *
 * Nos quatre tâches de code ne sont pas quatre échantillons du même travail : ce sont
 * quatre types de projet. Les moyenner effaçait ce qui distingue les modèles — DeepSeek
 * devance GLM-5.3-Flash de 9 points sur un site de contenu et lui rend 1 point sur une
 * application web. Un catalogue qui propose sept types de projet doit lire la mesure du
 * type demandé, pas leur milieu (Armand, 21/09/2026).
 *
 * Mêmes unités que BENCH (une note sur 50, ramenée à 1), même lien : les notes de R5 sont
 * ramenées sur la grille de R4 par le rapport des notes de GLM-5.3-Flash SUR CETTE TÂCHE.
 * Recalculé depuis data/ par tests/capability.test.ts — changer la mesure, pas ces nombres.
 *
 *                     opus-5 (R4)  deepseek   glm-5.3-flash
 *   french-a2            0,7875     0,6900       0,6000
 *   coffee-ecommerce     0,8474     0,8300       0,7800
 *   ged-webapp           0,7910     0,6600       0,6700
 *   instagram-mobile     0,7728     0,7300       0,6900
 */
export const BENCH_BY_TASK: Partial<Record<Domain, Record<string, Record<string, number>>>> = {
  coding_agent: {
    // R5 ramené sur la grille de R4 : note d'opus-5 × (flash R4 / flash R5), sur 50.
    'anthropic/claude-opus-5': {
      'french-a2': 42 / 50 * 30 / 32,
      'coffee-ecommerce': 44 / 50 * 39 / 40.5,
      'ged-webapp': 42.5 / 50 * 33.5 / 36,
      'instagram-mobile': 42 / 50 * 34.5 / 37.5,
    },
    'deepseek/deepseek-v4.1-flash': {
      'french-a2': 0.69, 'coffee-ecommerce': 0.83, 'ged-webapp': 0.66,
      'instagram-mobile': 0.73,
    },
    'z-ai/glm-5.3-flash': {
      'french-a2': 0.60, 'coffee-ecommerce': 0.78, 'ged-webapp': 0.67,
      'instagram-mobile': 0.69,
    },
  },
}

/**
 * Le modèle-témoin a passé les quatre tâches lui aussi : sa moyenne est une mesure comme
 * les autres, et la taire laissait sans note le modèle le moins cher de la grille.
 */

/**
 * Le modèle qui a passé les DEUX examens, et qui sert donc de point d'ancrage.
 *
 * Le règlement de ce fichier interdit de comparer dans un même classement des
 * notes issues de deux benchmarks différents. Sans note maison, deepseek-v4.1-flash
 * n'a aucune note sur le code (l'index publié ne le couvre pas) et sort de toute
 * recommandation. La note maison est donc RAMENÉE sur l'échelle publiée par le
 * rapport à claude-opus-5 : valeur = maison × 0,597 / maison(opus-5).
 *
 * Sources (data/bench-opus5-run2.json R5, data/bench-deepseek.json R4), tâches de code
 * (french-a2, coffee-ecommerce, ged-webapp, instagram-mobile), même grille figée, effort max :
 *   opus-5    − Flash, par tâche : +10,0 +3,5 +6,5 +4,5   moyenne +6,125
 *   deepseek  − Flash, par tâche :  +4,5 +2,5 −0,5 +2,0   moyenne +2,125
 *   rapport = (36,375/34,25) / (42,625/36,50) = 0,909  ->  0,543 (54/100)
 * Écart deepseek − opus-5 ramené : −4,0 pts/50 (−3,6 par le rapport), négatif sur les
 * quatre tâches (−5,5 −1,0 −7,0 −2,5). Apparié t = −2,92, ddl 3, p ≈ 0,06 ; Welch
 * t = −2,27, ddl 5,4, p ≈ 0,07 : à la limite, pas encore séparés.
 *
 * Pourquoi R5 seul, et plus R1 (12/09, 42,125 contre Flash 38,00, qui donnait 0,572) :
 * R1 était noté sur une grille pas encore figée, sous le protocole v1 sans installation
 * de dépendances que bench/PROTOCOLE.md a abandonné. Son écart d'opus-5 à Flash est plus
 * bas que celui de R5 sur les quatre tâches (+2,5 +2,0 +2,0 +1,5 ; apparié t = 9,8,
 * p ≈ 0,002) : un biais, pas un tirage. Une moyenne des deux (0,557 par le rapport,
 * 0,552 en additif) réinjecterait ce biais. R5 est la reprise, sur la grille figée, que
 * le panel précédent demandait.
 *
 * L'ancien couple {0,877 ; 0,525} venait d'un tour à 8 tâches, sur une grille
 * antérieure, via un harnais tiers (Hermes) en raisonnement MOYEN. Armand tient le
 * raisonnement maximal pour le réglage représentatif : ce tour est indicatif et ne
 * fixe plus rien.
 *
 * LE LIEN RESTE NÉCESSAIRE. Même grille, livrables inchangés, Flash monte de R4 à R5 de
 * +3,0 +2,5 +1,5 +2,0 (moyenne +2,25, écart-type 0,65 ; apparié t = 6,97, p ≈ 0,006) :
 * un effet de séance presque uniforme, ce que le rapport suppose et corrige. Sans lien
 * (42,625 contre 36,375 bruts), DeepSeek tomberait à 0,509 (51) et GLM-5.3 redeviendrait
 * la carte du milieu sur les quatre projets de code : le choix du lien décide des cartes.
 *
 * CE QUI EST LE MOINS FRAGILE : l'ORDRE sur nos tâches, et il reste indirect (lien
 * par GLM-5.3-Flash, sur deux séances). deepseek devance glm-5.3 de +6,75 pts/50
 * (Welch t = 3,19, ddl 4,7, p ≈ 0,03 ; p ≈ 0,16 corrigé pour les six paires). Il ne se
 * sépare pas de GLM-5.3-Flash (t apparié = 2,07, ddl 3, p ≈ 0,13) ; d'opus-5, à la
 * limite (ci-dessus) — faute de données, pas par égalité démontrée.
 *
 * CE QUE LA NOTE DÉCIDE. Par usage, DeepSeek tient « Le moins cher » quelle que soit sa note,
 * et « Qualité / Prix » tant qu'il s'affiche à 10 points ou moins de la meilleure note (62) :
 * 52 ou plus, soit une valeur ramenée d'au moins 0,515 ; 0,543 laisse une marge de 0,028.
 * Il faudrait DeepSeek à 34,50/50 (−1,87), opus-5 à 44,94 (+2,31) ou Flash R5 à 34,62
 * (−1,88) pour que GLM-5.3 prenne la carte — de l'ordre de l'effet de séance mesuré sur
 * Flash. Seule une séance où DeepSeek et opus-5 sont notés côte à côte trancherait.
 *
 * CE QUI NE L'EST PAS : la position sur l'échelle publiée. Le rapport suppose deux
 * échelles proportionnelles, et les trois modèles notés des deux côtés ne le confirment
 * pas — écarts à Flash chez nous : opus-5 +6,1, grok-4.6 +4,4, glm-5.3 −4,6 ; chez
 * Artificial Analysis : 0,597, 0,470, 0,536. Corrélation +0,13, et une inversion
 * grok/glm-5.3 (t = 2,75, p ≈ 0,04, tour de grok noté pendant que la grille changeait).
 * Le même rapport ancré sur grok-4.6 (seul ancrage au même harnais et au même effort)
 * donnerait 0,444 ; sur glm-5.3, 0,642. Incertitude de position : une dizaine de points
 * sur 100.
 *
 * Deux conséquences dans le code :
 *  - le rapport n'est PAS étendu à grok-4.6, glm-5.3 ni GLM-5.3-Flash. Avec R5 il ne
 *    placerait plus grok au-dessus d'opus-5 (0,575, contre 0,606 avec R1), mais il
 *    renverserait l'index sur grok et glm-5.3 (0,575 et 0,453, contre 0,470 et 0,536
 *    publiés), sur un tour de grok noté pendant que la grille changeait. Le choix entre
 *    ordre partiel et régression sur >= 3 points reste ouvert dans SAR-130 ;
 *  - une note ramenée est plafonnée un point SOUS la note affichée de l'ancre : à égalité
 *    affichée avec elle, la carte « Le meilleur » irait au moins cher, sur la foi d'une
 *    conversion et non d'une comparaison directe.
 */
const BENCH_ANCHOR = 'anthropic/claude-opus-5'

/**
 * A note that comes from OUR runs, converted, rather than from the index. The anchor has a bench
 * entry too, but it is converted onto itself and shows the published note: it is a reference
 * point, not a measurement to advertise.
 */
export function convertedFromBench(domain: Domain, modelId: string): boolean {
  return BENCH[domain]?.[modelId] !== undefined && modelId !== BENCH_ANCHOR
}

function benchScale(domain: Domain): number | null {
  const ours = BENCH[domain]?.[BENCH_ANCHOR]
  const theirs = DOMAINS[domain].scores[BENCH_ANCHOR]
  if (!ours || !theirs) return null
  return theirs / ours
}

/** The highest converted note that still prints below the anchor's. */
function belowAnchor(domain: Domain): number {
  return (scorePoints(DOMAINS[domain].scores[BENCH_ANCHOR]!) - 1) / 100
}

export type ScoreBasis = 'bench' | 'published' | 'none'

export interface Capability {
  /** 0..1, or null when nobody published and we did not measure. */
  value: number | null
  basis: ScoreBasis
}

/**
 * La note d'un modèle sur ce travail.
 *
 * `writingShare` bascule cette part du jugement sur l'indice généraliste : une
 * documentation s'écrit, et un classement de code ne dit rien de l'écriture. Le mélange
 * s'applique à TOUS les modèles notés de la même façon, donc la comparaison reste entre
 * pairs — et un modèle qui n'a pas les deux notes n'est pas mélangé, il reste hors
 * classement comme avant (Armand, 21/09/2026).
 */
export function capabilityOf(
  domain: Domain, modelId: string, writingShare = 0, task: string | null = null,
): Capability {
  if (writingShare > 0 && domain !== 'general') {
    const own = capabilityOf(domain, modelId, 0, task)
    const writing = capabilityOf('general', modelId)
    if (own.value === null || writing.value === null) return own
    return {
      value: own.value * (1 - writingShare) + writing.value * writingShare,
      basis: own.basis,
    }
  }
  // La mesure du travail demandé passe avant la moyenne des quatre.
  const own = (task === null ? undefined : BENCH_BY_TASK[domain]?.[modelId]?.[task])
    ?? BENCH[domain]?.[modelId]
  if (own !== undefined) {
    // Ramenée sur l'échelle publiée : sans ça, une moyenne maison sur 50 se
    // comparerait à un index qui ne mesure pas la même chose.
    const scale = benchScale(domain)
    if (scale === null) return { value: own, basis: 'bench' }
    const converted = own * scale
    return {
      value: modelId === BENCH_ANCHOR ? converted : Math.min(converted, belowAnchor(domain)),
      basis: 'bench',
    }
  }
  const published = DOMAINS[domain].scores[modelId]
  if (published !== undefined && published !== null) {
    return { value: published, basis: 'published' }
  }
  return { value: null, basis: 'none' }
}

/** The generalist index, for models the task-specific one does not cover. Shown
 *  greyed out and never ranked on: it is context, not evidence about this job. */
export function generalistOf(modelId: string): number | null {
  return GENERAL.scores[modelId] ?? null
}

/** 0..1 -> the 0..100 the sources themselves publish. */
export function scorePoints(value: number): number {
  return Math.round(value * 100)
}

export interface CostScorePoint {
  id: string
  costUsd: number
  score: number
  /** Compared in order, lower first, when prices are equal. Prices rarely tie per use and tie
   *  constantly per month, which is where this decides. */
  tiebreak?: number | readonly number[]
  /** Run through our own benchmark. Only ever a tie-break: on the same printed note, a model we
   *  have seen at work goes first. */
  tested?: boolean
  /** Place in the expert's order for this kind of work (0 first), when he gave one. */
  expertRank?: number | undefined
  /** Cleared the benchmark that set this kind of work's cap. Read only when a cap binds:
   *  once the cap has declared the field equal on quality, the top card stays among the
   *  models we have actually seen do the work. */
  capProven?: boolean
}

/**
 * Armand's verdict from use, for models the index cannot separate. Only ever a tie-break at the
 * top, and only between models it ranks: it settles a printed tie, it never outvotes a note.
 */
export const EXPERT_PREFERENCE: Partial<Record<Domain, { order: string[]; source_fr: string }>> = {
  // « Astra serait meilleure dans des tâches rédactionnelles, des slides… mais Fable reste
  // meilleure en design et en code. Mais ce n'est pas une super grosse différence. » (17/09/2026)
  coding_agent: {
    order: ['anthropic/claude-fable-5.1', 'openai/gpt-6-astra'],
    source_fr: 'Armand, à l’usage, 17/09/2026 : Fable 5.1 meilleur en design et en code',
  },
  general: {
    order: ['openai/gpt-6-astra', 'anthropic/claude-fable-5.1'],
    source_fr: 'Armand, à l’usage, 17/09/2026 : GPT-6 Astra meilleur en rédaction et en slides',
  },
}

export function expertRankOf(domain: Domain, modelId: string): number | undefined {
  const rank = EXPERT_PREFERENCE[domain]?.order.indexOf(modelId) ?? -1
  return rank < 0 ? undefined : rank
}

export type PickKind = 'cheapest' | 'best' | 'value'

/** What our own benchmark saw of a domain, for the cap that benchmark set. */
export interface CapBench {
  /** Models whose best run cleared the pass mark: the only ones the top card may name
   *  once the cap has declared the field equal. Having done the job once is enough to be
   *  NAMED; it is not enough to move the cap — see `steady`. */
  passed: string[]
  /** Models that cleared the mark on EVERY complete run. The cap is read off these: declaring
   *  a whole field equal on a model that did the job once in two would be a promise we have
   *  not measured. */
  steady: string[]
  /** Models that sat the benchmark and did not clear it. Named, never silently dropped. */
  failed: string[]
  runs: number
  models: number
  source_fr: string
}

/**
 * Our automation benchmark, 17-19/09/2026 (data/bench-automatisation.json, recomputed by
 * tests/cap.test.ts). Pass mark 94/100. Every priced model noted 30 or more cleared it on 23
 * runs out of 24; Haiku 4.5 (18) missed it twice, 89,61 then 92,59, missing the same
 * contradiction both times. One run can swing 9 points (Grok 4.6 at low: 86,11 then 95,28),
 * so a single run near the mark proves little — hence `steady`.
 *
 * The notes are the grid's, read at each model's most demanding setting, while most runs sat at
 * low. The gap is real: Artificial Analysis scores GPT-5.6 Terra 42 at max and 28 at low, and
 * Terra passed both its runs at low; GPT-5.6 Luna, off the grid, failed both of its own at low,
 * where the same index scores it 22. At the setting actually run, the floor lies between 22 and
 * 28. The cap stays on printed notes all the same — it is compared with them, and no grid model
 * prints a note between 18 and 30.
 *
 * This is a DIFFERENT fact from `tested` on a point, which means "we ran it on our nine coding
 * tasks": five models disagree between the two, which is why the cap reads this list and not
 * that flag.
 */
export const CAP_BENCH: Partial<Record<Domain, CapBench>> = {
  general: {
    passed: [
      'anthropic/claude-fable-5.1', 'anthropic/claude-opus-5', 'anthropic/claude-sonnet-5',
      'deepseek/deepseek-v4.1-flash', 'google/gemini-3.8-flash', 'minimax/minimax-m3',
      'openai/gpt-5.6-terra', 'x-ai/grok-4.6', 'z-ai/glm-5.3', 'z-ai/glm-5.3-flash',
    ],
    // One name is missing from this list and it is the point of the list: Grok 4.6 missed once
    // in four runs — 86,11 at low, 95,28 when low was run again, 95 at medium and xhigh. The
    // miss was a draw, not the setting, and a client would still meet it one time in four. It
    // stays named above — we have seen it work — and it does not set the cap.
    steady: [
      'anthropic/claude-fable-5.1', 'anthropic/claude-opus-5', 'anthropic/claude-sonnet-5',
      'deepseek/deepseek-v4.1-flash', 'google/gemini-3.8-flash', 'minimax/minimax-m3',
      'openai/gpt-5.6-terra', 'z-ai/glm-5.3', 'z-ai/glm-5.3-flash',
    ],
    failed: ['anthropic/claude-haiku-4.5'],
    runs: 32,
    models: 14,
    source_fr: 'notre bench d’automatisation, 32 runs sur 14 modèles, 17-20/09/2026',
  },
}

export function capProvenOn(domain: Domain, modelId: string): boolean {
  return CAP_BENCH[domain]?.passed.includes(modelId) ?? false
}

/** What a cap did to the three cards, for the page to say out loud. */
export interface CapOutcome {
  /** The printed note above which this kind of work shows no measurable gain. */
  points: number
  /** At least one model reaches it. Below that the cap changes nothing at all. */
  binds: boolean
  /** The top card went to a model our own benchmark cleared. */
  provenUsed: boolean
  /** The note the middle card has to reach: the cap, less the value gap. */
  floor: number
  /** Models printed above the cap and dearer than the top card — what the cap sets aside. */
  aside: string[]
  /** The model the top card would have named without the cap, when it differs. */
  instead: string | null
}

export interface PickSet {
  cheapest: string | null
  best: string | null
  /** The best value. Null only when nothing is priced. */
  value: string | null
  /** The non-dominated set, cheapest first: every model that is not both dearer
   *  and weaker than another. Everything outside it is a strictly worse deal. */
  frontier: string[]
  /** Null when this kind of work has no cap — then everything below behaves as it always did. */
  cap: CapOutcome | null
}

/** Every model that nothing in the grid beats on both axes at once. */
export function paretoFrontier(points: CostScorePoint[]): CostScorePoint[] {
  const byCost = [...points].sort((a, b) => a.costUsd - b.costUsd || b.score - a.score
    || compareTie(a, b))
  const frontier: CostScorePoint[] = []
  for (const point of byCost) {
    const last = frontier[frontier.length - 1]
    if (!last || point.score > last.score) frontier.push(point)
  }
  return frontier
}

/**
 * The three cards, each with one goal, as Armand defined them (17/09/2026):
 *
 *   Le moins cher    the lowest cost for the job; quality barely matters. The cheapest scored
 *                    model — on a price tie, the one that drains its quota slowest.
 *   Qualité / Prix   the best quality at the lowest cost. The cheapest model whose PRINTED
 *                    note is within `gap` of the top one. A pure note-per-euro ratio was
 *                    rejected: it names the cheapest model almost every time, 43/100 included.
 *   Le meilleur      the top note, whatever the price. On a printed tie, the expert's verdict
 *                    when it ranks at least two of the tied models, then a model we tested,
 *                    then the cheaper one.
 *
 * The cards are computed independently: one model may hold two of them, and the page shows
 * three cards every time. None of them filters a model on its completion record — that is a
 * fact the page prints, not a rule that decides. Notes are compared as printed, out of 100:
 * a decimal nobody sees never buys a dearer card.
 *
 * `capPoints` is the note above which OUR OWN benchmark stops measuring a gain on this kind of
 * work (`QUALITY_CAP_POINTS`). It changes nothing to the notes — a measurement is not rewritten
 * by a threshold we chose — only to what the cards read: above the cap the models count as
 * equal, so the top card takes the cheapest of them, and among those, the ones our benchmark
 * cleared. Armand asked for it in these words: recommending the dearest model for an automation
 * « n'a pas de sens, car ce serait overkill ». Null for a kind of work we never benchmarked.
 */
export function selectPicks(
  points: CostScorePoint[],
  gap: number,
  capPoints: number | null = null,
): PickSet {
  const usable = points.filter((p) => p.costUsd > 0)
  if (usable.length === 0) {
    return { cheapest: null, best: null, value: null, frontier: [], cap: null }
  }
  const printed = (p: CostScorePoint) => scorePoints(p.score)
  const untested = (p: CostScorePoint) => (p.tested ? 0 : 1)
  const effective = (p: CostScorePoint) =>
    (capPoints === null ? printed(p) : Math.min(printed(p), capPoints))

  const topNote = Math.max(...usable.map(effective))
  const binds = capPoints !== null && topNote >= capPoints
  // Above a binding cap the models count as equal, so an equal price is not settled on notes the
  // cap has just declared equal (45 against 42 is a gap the bench cannot see): it is settled on
  // whether our bench saw the model do this work. Without a cap, both terms reduce to the note.
  const unproven = (p: CostScorePoint) => (binds && !p.capProven ? 1 : 0)

  const cheapest = [...usable].sort((a, b) =>
    a.costUsd - b.costUsd || compareTie(a, b) || effective(b) - effective(a)
    || unproven(a) - unproven(b) || untested(a) - untested(b))[0]!
  let contenders = usable.filter((p) => effective(p) === topNote)
  let provenUsed = false
  let best: CostScorePoint
  if (binds) {
    // The cap has just declared these models equal on quality. The tie-breaks that exist to
    // separate them ON QUALITY — the expert's verdict, "a model we tested first" — have
    // nothing left to say, and must not spend on a difference we cannot measure. What is left
    // is the price, among the models we have actually seen do this work.
    const proven = contenders.filter((p) => p.capProven)
    provenUsed = proven.length > 0
    best = [...(provenUsed ? proven : contenders)].sort((a, b) =>
      a.costUsd - b.costUsd || compareTie(a, b) || printed(b) - printed(a)
      || untested(a) - untested(b))[0]!
  } else {
    // The expert order speaks only between the models it ranks: with two or more of them tied
    // at the top, the ones it did not put first leave the race.
    const ranked = contenders.filter((p) => p.expertRank !== undefined)
    if (ranked.length >= 2) {
      const favourite = Math.min(...ranked.map((p) => p.expertRank!))
      contenders = contenders.filter((p) =>
        p.expertRank === undefined || p.expertRank === favourite)
    }
    best = [...contenders].sort((a, b) =>
      untested(a) - untested(b) || a.costUsd - b.costUsd || compareTie(a, b))[0]!
  }
  // The floor hangs off the cap, not off the winner's own note: otherwise it sinks with the top
  // card and the middle card silently widens — it could then cost MORE than the top one.
  const floor = topNote - Math.round(gap * 100)
  const value = [...usable]
    .filter((p) => effective(p) >= floor)
    .sort((a, b) => a.costUsd - b.costUsd || effective(b) - effective(a)
      || unproven(a) - unproven(b) || untested(a) - untested(b) || compareTie(a, b))[0]!
  const uncapped = binds ? selectPicks(points, gap, null) : null
  return {
    cheapest: cheapest.id, best: best.id, value: value.id,
    // The frontier stays on the measured notes: "nothing is both cheaper and better" is a
    // fact, and a threshold we chose does not get to rewrite a fact.
    frontier: paretoFrontier(usable).map((p) => p.id),
    cap: capPoints === null ? null : {
      points: capPoints,
      binds,
      provenUsed,
      floor,
      aside: usable.filter((p) => printed(p) > capPoints && p.costUsd > best.costUsd)
        .map((p) => p.id),
      instead: uncapped && uncapped.best !== best.id ? uncapped.best : null,
    },
  }
}

function compareTie(a: CostScorePoint, b: CostScorePoint): number {
  const left = [a.tiebreak ?? 0].flat()
  const right = [b.tiebreak ?? 0].flat()
  for (let index = 0; index < Math.max(left.length, right.length); index++) {
    const x = left[index] ?? 0
    const y = right[index] ?? 0
    // Explicit comparisons, not subtraction: an unknown quota is Infinity, and
    // Infinity - Infinity is NaN, which a sort reads as "equal" at random.
    if (x < y) return -1
    if (x > y) return 1
  }
  return 0
}
